import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { t } from '@/locales/strings';
import { vibrateTap } from '@/utils/sound';

// ── Duotone tab icons ───────────────────────────────────────────────
//
// Each glyph is two layers: a soft current-color fill at 20% (only
// visible when the tab is active — the fill anchors the lit look),
// and a 1.5-stroke detail path that carries the silhouette. When
// inactive, the fill drops to 0 so the inactive tab reads as a
// pure outline, which keeps the active tab clearly distinct.
const SVG_BASE = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
};
const STROKE = {
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};
// Exported so the AppGuide bottom-sheet can render the same glyphs
// inline next to its destination rows. One icon language across
// surfaces means the user only learns the visual vocabulary once.
export function HouseIcon({ color, active }) {
  const fill = active ? `color-mix(in srgb, ${color} 22%, transparent)` : 'transparent';
  return (
    <svg {...SVG_BASE}>
      <path d="M4 11.2 12 4.5l8 6.7V20a1.2 1.2 0 0 1-1.2 1.2h-3.6V14.5h-6.4v6.7H5.2A1.2 1.2 0 0 1 4 20V11.2Z" fill={fill} />
      <path d="M3 12L12 4l9 8" stroke={color} {...STROKE} />
      <path d="M5 10.5v9a1 1 0 0 0 1 1h4v-6.5h4v6.5h4a1 1 0 0 0 1-1v-9" stroke={color} {...STROKE} />
    </svg>
  );
}
export function CalendarIcon({ color, active }) {
  const fill = active ? `color-mix(in srgb, ${color} 22%, transparent)` : 'transparent';
  return (
    <svg {...SVG_BASE}>
      <rect x="3" y="5" width="18" height="16" rx="2.5" fill={fill} />
      <rect x="3" y="5" width="18" height="16" rx="2.5" stroke={color} {...STROKE} />
      <path d="M3 10h18" stroke={color} {...STROKE} />
      <path d="M8 3v4M16 3v4" stroke={color} {...STROKE} />
      {/* Two small day-dots inside the grid, only visible when lit */}
      {active && (
        <>
          <circle cx="8.5" cy="14.5" r="1" fill={color} opacity="0.85" />
          <circle cx="12" cy="14.5" r="1" fill={color} opacity="0.55" />
          <circle cx="15.5" cy="14.5" r="1" fill={color} opacity="0.35" />
        </>
      )}
    </svg>
  );
}
export function BookIcon({ color, active }) {
  const fill = active ? `color-mix(in srgb, ${color} 22%, transparent)` : 'transparent';
  return (
    <svg {...SVG_BASE}>
      <path d="M4 5.5a1.5 1.5 0 0 1 1.5-1.5H11v14.5H5.5a1.5 1.5 0 0 1-1.5-1.5V5.5Z" fill={fill} />
      <path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v14.5h5.5a1.5 1.5 0 0 0 1.5-1.5V5.5Z" fill={fill} />
      <path d="M4 5.5a1.5 1.5 0 0 1 1.5-1.5H11v14.5H5.5a1.5 1.5 0 0 1-1.5-1.5V5.5Z" stroke={color} {...STROKE} />
      <path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v14.5h5.5a1.5 1.5 0 0 0 1.5-1.5V5.5Z" stroke={color} {...STROKE} />
      {/* Spine accent — subtle mid-line that ties the two pages */}
      <path d="M12 4v14.5" stroke={color} strokeWidth="1" opacity={active ? 0.6 : 0.45} strokeLinecap="round" />
    </svg>
  );
}
export function PersonIcon({ color, active }) {
  const fill = active ? `color-mix(in srgb, ${color} 22%, transparent)` : 'transparent';
  return (
    <svg {...SVG_BASE}>
      <circle cx="12" cy="8.5" r="3.6" fill={fill} />
      <path d="M5 20c0-3.4 3.1-6 7-6s7 2.6 7 6Z" fill={fill} />
      <circle cx="12" cy="8.5" r="3.6" stroke={color} {...STROKE} />
      <path d="M5 20c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke={color} {...STROKE} />
    </svg>
  );
}
// Three concentric rings — mirrors the player's RingPlayer canal so
// the Train tab visually anchors "this is where the practice lives".
export function TrainIcon({ color, active }) {
  const fill = active ? `color-mix(in srgb, ${color} 22%, transparent)` : 'transparent';
  return (
    <svg {...SVG_BASE}>
      <circle cx="12" cy="12" r="9" fill={fill} />
      <circle cx="12" cy="12" r="9" stroke={color} {...STROKE} />
      <circle cx="12" cy="12" r="6" stroke={color} {...STROKE} />
      <circle cx="12" cy="12" r="3" stroke={color} {...STROKE} fill={active ? color : 'transparent'} />
    </svg>
  );
}
// Soft heart — universally readable wellbeing glyph for the Body
// tab. Tuned with a slightly tucked dip so it reads as a heart, not
// a bow, at the 22px tab size.
export function BodyIcon({ color, active }) {
  const fill = active ? `color-mix(in srgb, ${color} 22%, transparent)` : 'transparent';
  return (
    <svg {...SVG_BASE}>
      <path
        d="M12 20.5s-7-4.5-7-10A4 4 0 0 1 12 7.5 4 4 0 0 1 19 10.5c0 5.5-7 10-7 10z"
        fill={fill}
      />
      <path
        d="M12 20.5s-7-4.5-7-10A4 4 0 0 1 12 7.5 4 4 0 0 1 19 10.5c0 5.5-7 10-7 10z"
        stroke={color}
        {...STROKE}
      />
    </svg>
  );
}

// ── BottomNav (frosted glass, rounded top) ──

export default function BottomNav({ lang }) {
  const location = useLocation();
  const navigate = useNavigate();
  const current = location.pathname;

  // 5-tab bottom nav (IA migration step 6, PR #159 section 3.2):
  //   Today  - anchor (today's session, streak, reflection prompt)
  //   Train  - the Method journey + practice surfaces
  //   Body   - cycle, peri/meno, libido (the "state of my body" tab)
  //   Learn  - articles, anatomy, research library
  //   Me     - account, settings, legal
  //
  // /calendar drops from the bar but stays reachable via deep-link
  // and AppGuide so existing reminder URLs and bookmarks don't 404.
  // Each tab still routes to an existing landing screen; the Train
  // landing (a dedicated /train shell) is a follow-up.
  const tabs = [
    { path: '/home', label: t('nav_today', lang), icon: HouseIcon },
    { path: '/train', label: t('nav_train', lang), icon: TrainIcon },
    { path: '/body', label: t('nav_body', lang), icon: BodyIcon },
    { path: '/learning', label: t('nav_learn', lang), icon: BookIcon },
    { path: '/profile', label: t('nav_me', lang), icon: PersonIcon },
  ];

  return (
    <nav style={{
      // Natural flex child at the bottom of the screen's flex-column
      // layout — not position:fixed. Mobile browsers (especially iOS
      // Chrome with a bottom URL bar and `viewport-fit=cover`) were
      // anchoring the fixed nav below the visible viewport, so the
      // tabs got covered by the browser chrome. As a flex child, it
      // always sits at the bottom of its 100dvh container, above any
      // browser chrome the viewport reports.
      flexShrink: 0,
      zIndex: 100,
      // Frosted glass — backdrop-filter on a slightly-transparent
      // surface so content scrolling underneath is softly tinted.
      // Falls back to the solid surface colour on browsers without
      // backdrop-filter (older Firefox, older Android WebViews).
      background: 'color-mix(in srgb, var(--surface) 82%, transparent)',
      backdropFilter: 'saturate(180%) blur(20px)',
      WebkitBackdropFilter: 'saturate(180%) blur(20px)',
      borderTop: '1px solid color-mix(in srgb, var(--border) 70%, transparent)',
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      padding: '10px 16px var(--safe-bottom)',
      width: '100%',
      maxWidth: 520,
      marginLeft: 'auto',
      marginRight: 'auto',
      boxShadow: '0 -4px 20px -8px rgba(45, 26, 31, 0.08)',
    }}>
      {tabs.map(({ path, label, icon: Icon }) => {
        // Match exact tab path or any deeper child route under it
        // (so Method stays lit on /method/foundation/week/1, not just
        // /method). Guarding with a trailing slash so /home doesn't
        // claim /home-something hypothetical sub-routes.
        const isActive = current === path || current.startsWith(path + '/');
        const activeColor = 'var(--primary)';
        const inactiveColor = 'var(--warm-brown)';
        return (
          <button
            key={path}
            type="button"
            className="tap-card"
            onClick={() => {
              // Skip when the user taps the tab she is already on.
              // navigate() to the same path would still trigger the
              // viewTransition crossfade via React Router v7, which
              // reads as a flicker rather than a state change.
              if (isActive) return;
              vibrateTap();
              // viewTransition: true flips React Router v7 into using
              // document.startViewTransition() around the route commit
              // so the tab swap crossfades at the browser level instead
              // of the default cut. Chrome / Edge / Safari 18+ render
              // the transition; older browsers silently fall through.
              navigate(path, { viewTransition: true });
            }}
            aria-current={isActive ? 'page' : undefined}
            aria-label={label}
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              // 5-tab layout: tighter horizontal padding so each tab
              // fits on a 430px viewport without wrapping the label.
              padding: '8px 8px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: isActive ? activeColor : inactiveColor,
              opacity: isActive ? 1 : 0.65,
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
              touchAction: 'manipulation',
              minHeight: 44,
              minWidth: 44,
            }}
          >
            {/* Active indicator — a soft primary-pale disc behind the
                icon. Apple's iOS-26 tab pattern: the active tab is
                "lit" by a tinted glyph background. Inactive tabs sit
                with no chrome so the eye lands cleanly on the active
                one. The disc scale-pops in (bb-nav-pop) on activation. */}
            {isActive && (
              <span
                aria-hidden="true"
                className="bb-nav-pop"
                style={{
                  position: 'absolute',
                  top: 4,
                  width: 44,
                  height: 32,
                  borderRadius: 999,
                  background: 'var(--primary-pale)',
                  boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--primary) 20%, transparent)',
                  pointerEvents: 'none',
                }}
              />
            )}
            {/* pointerEvents: none on the inner visuals so a tap on the
                icon or label always bubbles to the button's onClick,
                even on Safari where nested <svg> children can otherwise
                swallow the synthetic click. */}
            <span style={{ pointerEvents: 'none', display: 'inline-flex', position: 'relative', zIndex: 1 }}>
              <Icon color={isActive ? activeColor : inactiveColor} active={isActive} />
            </span>
            <span style={{
              pointerEvents: 'none',
              position: 'relative',
              zIndex: 1,
              fontFamily: 'var(--font-ui)',
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}>
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
